"use client";

import { useState, useEffect, useRef } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import Link from "next/link";
import { ArrowRight, Star, Copy, Check, Sparkles } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? "";
const FREE_USES_BEFORE_CAPTCHA = 3;
const COUNTER_KEY = "otr_free_tool_uses";
const TURNSTILE_REQUIRED_FROM_FIRST_USE = process.env.NODE_ENV === "production";

export default function ReviewResponseGeneratorPage() {
  const [reviewText, setReviewText] = useState("");
  const [rating, setRating] = useState(5);
  const [businessName, setBusinessName] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [useCount, setUseCount] = useState(0);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | undefined>(undefined);

  useEffect(() => {
    const stored = parseInt(localStorage.getItem(COUNTER_KEY) ?? "0", 10);
    setUseCount(stored);
  }, []);

  const needsCaptcha =
    TURNSTILE_REQUIRED_FROM_FIRST_USE || useCount >= FREE_USES_BEFORE_CAPTCHA;

  function resetTurnstile() {
    if (!needsCaptcha) return;
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  }

  async function handleGenerate() {
    if (!reviewText.trim()) return setError("Please enter the review text.");
    if (needsCaptcha && !turnstileToken)
      return setError("Please complete the CAPTCHA.");
    setLoading(true);
    setError("");
    setReply("");
    try {
      const res = await fetch("/api/tools/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewText,
          rating,
          businessName: businessName || undefined,
          turnstileToken: turnstileToken || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Generation failed");
      setReply(data.reply);
      const next = useCount + 1;
      setUseCount(next);
      localStorage.setItem(COUNTER_KEY, String(next));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      resetTurnstile();
      setLoading(false);
    }
  }

  function handleCopy() {
    void navigator.clipboard.writeText(reply);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden bg-white pt-32 pb-12 sm:pt-40 sm:pb-16 aura">
          <div className="relative z-10 mx-auto max-w-2xl px-6">
            <p className="ribbon mb-3 text-ink-500">Free tool</p>
            <h1 className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-ink-950 sm:text-5xl text-balance">
              AI review reply generator.{" "}
              <em className="font-display italic font-normal text-brand-600">
                Free,
              </em>{" "}
              no signup.
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-ink-600 text-pretty">
              Paste a Google review below, get a professional reply in seconds.
            </p>
          </div>
        </section>

        {/* Tool form */}
        <section className="bg-white pb-16">
          <div className="mx-auto max-w-2xl px-6">
            <div className="rounded-2xl border border-ink-100 bg-white p-6 shadow-sm sm:p-8">
              <div className="space-y-5">
                <div>
                  <label
                    htmlFor="review"
                    className="block text-sm font-semibold text-ink-800"
                  >
                    Review text
                  </label>
                  <textarea
                    id="review"
                    rows={4}
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value)}
                    placeholder="Paste the Google review here..."
                    className="mt-2 w-full rounded-xl border border-ink-200 px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-ink-800">
                    Star rating
                  </label>
                  <div className="mt-2 flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        className="rounded-lg p-1 outline-none focus:ring-2 focus:ring-brand-200"
                      >
                        <Star
                          className={`h-7 w-7 ${
                            n <= rating
                              ? "fill-sig-amber text-sig-amber"
                              : "fill-ink-100 text-ink-200"
                          }`}
                          aria-hidden="true"
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="biz"
                    className="block text-sm font-semibold text-ink-800"
                  >
                    Business name{" "}
                    <span className="font-normal text-ink-400">(optional)</span>
                  </label>
                  <input
                    id="biz"
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="e.g. Joe's Pizza"
                    className="mt-2 w-full rounded-xl border border-ink-200 px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                  />
                </div>

                {needsCaptcha && (
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={TURNSTILE_SITE_KEY}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken(null)}
                    onError={() => setTurnstileToken(null)}
                  />
                )}

                {error && (
                  <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="approve-btn flex w-full items-center justify-center gap-2 rounded-xl bg-brand-600 px-6 py-3.5 text-sm font-bold text-white shadow-md hover:bg-brand-700 disabled:opacity-60"
                >
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                  {loading ? "Generating..." : "Generate reply"}
                </button>
              </div>
            </div>

            {reply && (
              <div className="mt-6 rounded-2xl border border-ink-100 bg-ink-50 p-6">
                <div className="mb-3 flex items-center justify-between">
                  <p className="ribbon text-ink-500">Generated reply</p>
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 hover:text-brand-700"
                  >
                    {copied ? (
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <p className="whitespace-pre-wrap text-ink-800 text-pretty">
                  {reply}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Long-form content */}
        <section className="bg-white py-16 sm:py-20">
          <div className="mx-auto max-w-2xl space-y-14 px-6">
            <div>
              <p className="ribbon mb-3 text-ink-500">How it works</p>
              <h2 className="font-display text-2xl font-bold tracking-tight text-ink-950 sm:text-3xl text-balance">
                Paste, pick a rating, get a reply.
              </h2>
              <p className="mt-4 text-ink-700 leading-relaxed text-pretty">
                Paste any Google review, select the star rating, and our AI
                generates a professional, context-aware reply in seconds. The
                model reads sentiment, identifies key topics, and crafts a
                response that feels personal — not templated.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed text-pretty">
                For positive reviews, it thanks the customer for what they
                praised and reinforces the experience. For negative reviews, it
                acknowledges the concern, apologizes where appropriate, and
                offers a path to resolution. The reply matches the language of
                the review automatically — 50+ supported.
              </p>
            </div>

            <div>
              <p className="ribbon mb-3 text-ink-500">Why it matters</p>
              <h2 className="font-display text-2xl font-bold tracking-tight text-ink-950 sm:text-3xl text-balance">
                Replying isn&apos;t optional.
              </h2>
              <p className="mt-4 text-ink-700 leading-relaxed text-pretty">
                Google has confirmed that review responses are a ranking factor
                for local search. Businesses that respond consistently appear
                higher in Google Maps and the local pack. Beyond SEO, your
                replies are read by every potential customer researching your
                business — 88% say they&apos;re more likely to visit a business
                that responds to reviews.
              </p>
              <p className="mt-4 text-ink-700 leading-relaxed text-pretty">
                Speed matters. Responding within hours signals to both Google
                and customers that you&apos;re an active, engaged business.
                Within 24 hours is the bar.
              </p>
            </div>

            <div>
              <p className="ribbon mb-3 text-ink-500">Tips</p>
              <h2 className="font-display text-2xl font-bold tracking-tight text-ink-950 sm:text-3xl text-balance">
                Five things to do (and not).
              </h2>
              <ul className="mt-6 space-y-4">
                {[
                  "Personalize — mention the reviewer's name and reference specific details from their review.",
                  "Keep it concise — 2–4 sentences is the sweet spot. Long replies feel corporate and go unread.",
                  "Never argue with negative reviewers — acknowledge their experience and offer to make it right offline.",
                  "Include a soft call-to-action — invite happy customers back or mention an upcoming special.",
                  "Respond to every review, not just negative ones — this shows future customers you care about all feedback.",
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 text-sm font-bold text-brand-700">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 text-ink-700 text-pretty">
                      {tip}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Final CTA — Full automation */}
        <section className="relative overflow-hidden bg-ink-950 py-20 text-white sm:py-28 aura">
          <div className="relative z-10 mx-auto max-w-3xl px-6 text-center">
            <p className="ribbon mb-4 text-ink-300">Want this automated?</p>
            <h2 className="font-display text-4xl font-bold tracking-tight text-white sm:text-5xl text-balance">
              Skip the copy-paste.{" "}
              <em className="font-display italic font-normal text-sig-yellow">
                For every review.
              </em>
            </h2>
            <p className="mt-5 text-lg text-ink-300 text-pretty">
              OneTapReply monitors your Google reviews 24/7, drafts replies,
              and sends them to your dashboard and email with Approve, Edit,
              and Skip actions.
            </p>
            <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                href="/auth/signin"
                className="approve-btn inline-flex items-center gap-2 rounded-xl bg-white px-7 py-4 text-base font-bold text-ink-950 shadow-lg hover:bg-ink-100"
              >
                Start free
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link
                href="/pricing"
                className="inline-flex items-center gap-2 rounded-xl border border-ink-700 bg-ink-900/60 px-7 py-4 text-base font-bold text-white hover:bg-ink-800"
              >
                See pricing
              </Link>
            </div>
            <p className="mt-5 text-xs text-ink-400">
              Free up to 3 posts every 30 days · No credit card · Cancel anytime
            </p>
          </div>
        </section>

        <script
          type="application/ld+json"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              name: "Free AI Google Review Response Generator",
              url: "https://onetapreply.com/tools/review-response-generator",
              applicationCategory: "BusinessApplication",
              operatingSystem: "All",
              offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
              description:
                "Generate professional AI-powered replies to Google reviews for free. No signup required.",
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}
